import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Link as LinkIcon, Clock, Shield, FileText } from "lucide-react";

/**
 * Accounting Integrations panel — visually identical to the VAT Vault's
 * "Connect Accounting Software" block so the experience is consistent across
 * the Invoices hub, VAT Vault, and anywhere else we drop it in.
 *
 * Cards are intentionally rendered at 50% opacity behind a centered
 * "Coming Soon" overlay until backend integrations are wired up.
 */
const ProviderCard = ({ name, description, color, initials }) => (
  <Card className="border border-gray-200 shadow-sm">
    <CardContent className="p-4 text-center">
      <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold font-heading shadow-sm ${color}`}>
        {initials}
      </div>
      <h3 className="font-semibold text-gray-900 font-heading">{name}</h3>
      <p className="text-xs text-gray-500 mt-1 font-body leading-snug">{description}</p>
      <Button variant="outline" size="sm" disabled className="mt-3 w-full">
        Connect
      </Button>
    </CardContent>
  </Card>
);

export const AccountingIntegrationsPanel = () => {
  const providers = [
    { name: "Xero", description: "Connect your Xero account for automatic invoice sync", color: "bg-blue-600", initials: "X" },
    { name: "QuickBooks", description: "Sync QuickBooks invoices and tax data", color: "bg-green-600", initials: "QB" },
    { name: "FreeAgent", description: "Import FreeAgent invoices automatically", color: "bg-purple-600", initials: "FA" },
    { name: "Sage", description: "Connect Sage for comprehensive invoicing", color: "bg-yellow-600", initials: "S" },
  ];

  return (
    <Card className="border-0 shadow-sm mb-8" data-testid="accounting-integrations-panel">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2 font-heading">
          <LinkIcon className="w-5 h-5" />
          <span>Connect Accounting Software</span>
          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            Coming Soon
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Coming Soon overlay */}
          <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <p className="font-semibold text-gray-900 font-heading">Coming Soon</p>
              <p className="text-sm text-gray-600 font-body mt-1">Accounting integrations are on the way!</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 opacity-50">
            {providers.map((p) => (
              <ProviderCard key={p.name} {...p} />
            ))}
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start space-x-2">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 font-heading">Secure Integration</h4>
              <p className="text-sm text-blue-700 font-body">
                OAuth2 authentication • Read-only access • HMRC compliant • Real-time sync via webhooks
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AccountingIntegrationsPanel;
